/**
 * Per-size availability history.
 *
 * Every row shares one time domain (earliest variant first-seen → now) so the
 * sizes can be compared against each other at a glance. Segments come from the
 * variant-restock / variant-sold-out events; the trailing segment is pinned to
 * the variant's current `available` flag, which is the authoritative "now".
 */
import { useMemo } from "react";
import { pluralize, relativeTime } from "../lib/format";
import type { Variant, WatchEvent } from "../lib/types";
import { fullStamp } from "./EventRow";

export interface StockTimelineProps {
  variants: Variant[];
  events: WatchEvent[];
}

type Kind = "in" | "out" | "unseen";

interface Segment {
  key: string;
  kind: Kind;
  start: number;
  end: number;
}

interface Row {
  variantId: number;
  size: string;
  available: boolean;
  gone: boolean;
  restockCount: number;
  changes: number;
  firstSeen: string;
  lastSeen: string;
  lastRestockedAt?: string;
  lastSoldOutAt?: string;
  segments: Segment[];
}

const DAY = 86_400_000;

/** Canonical apparel order; anything unrecognised falls back to variant position. */
const SIZE_ORDER = [
  "OS", "ONE SIZE", "XXS", "XS", "S", "SM", "M", "MD", "L", "LG",
  "XL", "1XL", "XXL", "2XL", "XXXL", "3XL",
];

function sizeRank(title: string): number {
  const index = SIZE_ORDER.indexOf(title.trim().toUpperCase());
  return index === -1 ? SIZE_ORDER.length : index;
}

function parse(iso?: string): number {
  if (!iso) return NaN;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : NaN;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

const KIND_LABEL: Record<Kind, string> = {
  in: "In stock",
  out: "Sold out",
  unseen: "Not tracked",
};

function segmentTitle(size: string, segment: Segment): string {
  return `${size} · ${KIND_LABEL[segment.kind]} · ${fullStamp(
    new Date(segment.start).toISOString(),
  )} → ${fullStamp(new Date(segment.end).toISOString())}`;
}

function buildSegments(
  variant: Variant,
  transitions: Array<{ at: number; toAvailable: boolean }>,
  domainStart: number,
  domainEnd: number,
): Segment[] {
  const rawFirst = parse(variant.firstSeen);
  const trackStart = clamp(Number.isNaN(rawFirst) ? domainStart : rawFirst, domainStart, domainEnd);
  const rawLast = parse(variant.lastSeen);
  const trackEnd = variant.gone
    ? clamp(Number.isNaN(rawLast) ? domainEnd : rawLast, trackStart, domainEnd)
    : domainEnd;

  const segments: Segment[] = [];
  if (trackStart > domainStart) {
    segments.push({ key: "pre", kind: "unseen", start: domainStart, end: trackStart });
  }

  const points = transitions
    .map((t) => ({ ...t, at: clamp(t.at, trackStart, trackEnd) }))
    .sort((a, b) => a.at - b.at);

  // The state before the first transition is the inverse of what it produced.
  let state = points.length > 0 ? !points[0].toAvailable : variant.available;
  let cursor = trackStart;

  for (let i = 0; i < points.length; i += 1) {
    const point = points[i];
    if (point.at > cursor) {
      segments.push({ key: `s${i}`, kind: state ? "in" : "out", start: cursor, end: point.at });
    }
    cursor = point.at;
    state = point.toAvailable;
  }

  segments.push({
    key: "tail",
    kind: variant.available && !variant.gone ? "in" : "out",
    start: cursor,
    end: trackEnd,
  });

  if (trackEnd < domainEnd) {
    segments.push({ key: "post", kind: "unseen", start: trackEnd, end: domainEnd });
  }

  return segments;
}

const CSS = `
.awstk{--ink:#0D0D0D;--red:#FF0000;--muted:#8A8A8A;--line:rgba(13,13,13,.14)}
.awstk-empty{
  margin:0;padding:18px 0;
  font-family:"Sora","Helvetica Neue",Arial,sans-serif;
  font-size:12px;line-height:1.6;color:#8A8A8A;
}
.awstk-rows{list-style:none;margin:0;padding:0}
.awstk-row{
  display:grid;
  grid-template-columns:58px minmax(0,1fr) 88px;
  column-gap:12px;
  row-gap:5px;
  align-items:center;
  padding:11px 0;
  border-bottom:1px solid var(--line);
}
.awstk-row:last-child{border-bottom:none}
.awstk-size{
  font-family:"Oswald","Arial Narrow",sans-serif;
  font-size:12px;letter-spacing:.12em;text-transform:uppercase;color:var(--ink);
}
.awstk-band{
  display:flex;height:16px;width:100%;
  border:1px solid var(--line);background:#FFFFFF;overflow:hidden;
}
.awstk-seg{display:block;min-width:2px;flex-basis:0}
.awstk-seg.awstk-in{background:#0D0D0D}
.awstk-seg.awstk-out{
  background:repeating-linear-gradient(135deg,rgba(255,0,0,.30) 0 3px,rgba(255,0,0,.07) 3px 7px);
}
.awstk-seg.awstk-unseen{
  background:repeating-linear-gradient(135deg,#DCDCDC 0 2px,#F2F2F2 2px 6px);
}
.awstk-state{
  justify-self:end;
  font-family:"Oswald","Arial Narrow",sans-serif;
  font-size:11px;letter-spacing:.1em;text-transform:uppercase;white-space:nowrap;
}
.awstk-state.is-in{color:var(--ink)}
.awstk-state.is-out{color:var(--red)}
.awstk-state.is-gone{color:var(--muted)}
.awstk-meta{
  grid-column:2 / -1;
  margin:0;
  font-family:"Sora","Helvetica Neue",Arial,sans-serif;
  font-size:10.5px;line-height:1.55;color:var(--muted);
}
.awstk-axis{
  display:flex;justify-content:space-between;gap:12px;
  margin-top:10px;padding-top:8px;border-top:1px solid var(--line);
  font-family:"Sora","Helvetica Neue",Arial,sans-serif;
  font-size:10px;letter-spacing:.06em;text-transform:uppercase;color:var(--muted);
}
.awstk-legend{
  list-style:none;display:flex;flex-wrap:wrap;gap:6px 16px;margin:10px 0 0;padding:0;
  font-family:"Sora","Helvetica Neue",Arial,sans-serif;
  font-size:10px;letter-spacing:.06em;text-transform:uppercase;color:var(--muted);
}
.awstk-legend li{display:flex;align-items:center;gap:6px}
.awstk-swatch{width:18px;height:10px;border:1px solid var(--line);display:block}

@media (max-width:640px){
  .awstk-row{grid-template-columns:46px minmax(0,1fr);}
  .awstk-state{grid-column:2;justify-self:start}
  .awstk-meta{grid-column:1 / -1}
}
`;

const STYLE_ID = "aw-stock-timeline-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const tag = document.createElement("style");
  tag.id = STYLE_ID;
  tag.textContent = CSS;
  document.head.appendChild(tag);
}

export function StockTimeline({ variants, events }: StockTimelineProps) {
  const { rows, domainStart, domainEnd } = useMemo(() => {
    const now = Date.now();

    const seen = variants.map((v) => parse(v.firstSeen)).filter((t) => !Number.isNaN(t));
    const start = seen.length > 0 ? Math.min(...seen) : now - DAY;
    const end = Math.max(now, start + 60_000);

    const transitionEvents = events.filter(
      (e) => e.type === "variant-restock" || e.type === "variant-sold-out",
    );

    const built = variants.map<Row>((variant) => {
      const size = variant.title || "—";
      const mine = transitionEvents.filter((e) =>
        e.variantId !== undefined ? e.variantId === variant.variantId : e.size === variant.title,
      );
      const transitions = mine
        .map((e) => ({ at: parse(e.occurredAt), toAvailable: e.type === "variant-restock" }))
        .filter((t) => !Number.isNaN(t.at));

      return {
        variantId: variant.variantId,
        size,
        available: variant.available,
        gone: variant.gone,
        restockCount: variant.restockCount,
        changes: transitions.length,
        firstSeen: variant.firstSeen,
        lastSeen: variant.lastSeen,
        lastRestockedAt: variant.lastRestockedAt,
        lastSoldOutAt: variant.lastSoldOutAt,
        segments: buildSegments(variant, transitions, start, end),
      };
    });

    built.sort((a, b) => sizeRank(a.size) - sizeRank(b.size) || a.size.localeCompare(b.size));

    return { rows: built, domainStart: start, domainEnd: end };
  }, [variants, events]);

  if (variants.length === 0) {
    return (
      <div className="awstk">
        <p className="awstk-empty">
          No sizes recorded yet. The watcher stores per-size history the first time it reads this
          product&rsquo;s variants.
        </p>
      </div>
    );
  }

  return (
    <div className="awstk">
      <ul className="awstk-rows">
        {rows.map((row) => {
          const state = row.gone ? "gone" : row.available ? "in" : "out";
          const stateText = row.gone ? "Gone" : row.available ? "In stock" : "Sold out";

          const meta: string[] = [];
          if (row.changes === 0) {
            meta.push(
              row.gone
                ? `No stock change was ever recorded — last seen ${fullStamp(row.lastSeen)}.`
                : `No change since ${fullStamp(row.firstSeen)} — ${
                    row.available ? "in stock" : "sold out"
                  } the whole time the watcher has known it.`,
            );
          } else {
            meta.push(`${row.changes} ${pluralize(row.changes, "change")} tracked`);
            if (row.restockCount > 0) {
              meta.push(`${row.restockCount} ${pluralize(row.restockCount, "restock")}`);
            }
            if (row.lastRestockedAt) meta.push(`back ${relativeTime(row.lastRestockedAt)}`);
            if (row.lastSoldOutAt) meta.push(`out ${relativeTime(row.lastSoldOutAt)}`);
          }

          return (
            <li key={row.variantId} className="awstk-row">
              <span className="awstk-size">{row.size}</span>
              <div
                className="awstk-band"
                role="img"
                aria-label={`${row.size}: ${stateText}, ${row.changes} recorded ${pluralize(
                  row.changes,
                  "change",
                )}`}
              >
                {row.segments.map((segment) => (
                  <span
                    key={segment.key}
                    className={`awstk-seg awstk-${segment.kind}`}
                    style={{ flexGrow: Math.max(segment.end - segment.start, 1) }}
                    title={segmentTitle(row.size, segment)}
                  />
                ))}
              </div>
              <span className={`awstk-state is-${state}`}>{stateText}</span>
              <p className="awstk-meta">{meta.join(" · ")}</p>
            </li>
          );
        })}
      </ul>

      <div className="awstk-axis">
        <span>{fullStamp(new Date(domainStart).toISOString())}</span>
        <span title={fullStamp(new Date(domainEnd).toISOString())}>Now</span>
      </div>

      <ul className="awstk-legend">
        <li>
          <span className="awstk-swatch awstk-seg awstk-in" /> In stock
        </li>
        <li>
          <span className="awstk-swatch awstk-seg awstk-out" /> Sold out
        </li>
        <li>
          <span className="awstk-swatch awstk-seg awstk-unseen" /> Not tracked
        </li>
      </ul>
    </div>
  );
}

export default StockTimeline;
